import produce from 'immer';
import {
  ContextControl,
  ContextControlSearchParam,
  Controls,
  ControlsSearchParams,
  FilterControl,
  FilterControlSearchParam,
  PeriodControl,
} from 'lib-analytics-types';
import { DeepWritable } from 'ts-essentials';

const DELIMITER = {
  FIELD: '~',
  VALUES: ',',
};

const makeControl = <
  TControlSearchParam extends
    | ContextControlSearchParam
    | FilterControlSearchParam,
  TControl extends ContextControl | FilterControl,
>(
  controlSearchParam: TControlSearchParam,
): DeepWritable<TControl> => {
  const [field, operator, valueStr] = controlSearchParam.split(DELIMITER.FIELD);

  const values: ContextControl['values'] = valueStr
    ? valueStr.split(DELIMITER.VALUES).map((value) => ({ value }))
    : [];

  return { field, values, operator } as DeepWritable<TControl>;
};

export const parseControlsFromSearchParams = (
  searchParams: ControlsSearchParams,
): Partial<Controls> =>
  produce<Partial<Controls>>({}, (controls) => {
    if (searchParams.context) {
      controls.context = makeControl<ContextControlSearchParam, ContextControl>(
        searchParams.context,
      );
    }

    if (searchParams.filters?.length) {
      controls.filters = searchParams.filters.map((filter) =>
        makeControl<FilterControlSearchParam, FilterControl>(filter),
      );
    }

    if (searchParams.period) {
      const values = searchParams.period
        .split(DELIMITER.VALUES)
        .map((value) => ({ value }));

      // There can only be at most two values for the period control: a preset like "today" or a
      // date range of two ISO dates.
      if (values.length <= 2) {
        controls.period = {
          field: 'created_at',
          values,
        } as DeepWritable<PeriodControl>;
      }
    }
  });
